import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Lock, Unlock, AlertCircle, CheckCircle, TrendingUp,
  Banknote, CreditCard, ArrowRightLeft, Clock, X,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getErrorMessage } from '@/lib/errors';

type SesionCaja = {
  id: string;
  estado: 'abierta' | 'cerrada';
  fondoInicial: number;
  totalEfectivo: number | null;
  totalTarjeta: number | null;
  totalTransferencia: number | null;
  totalGeneral: number | null;
  observaciones: string | null;
  fechaCierre: string | null;
  createdAt: string;
  usuario?: { nombre: string; apellidoPaterno: string };
};

type Resumen = {
  sesion: SesionCaja;
  totalEfectivo: number;
  totalTarjeta: number;
  totalTransferencia: number;
  cantidadOperaciones: number;
};

function fmt(n: number) {
  return Number(n).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
}
function fmtDate(s: string) {
  return new Date(s).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' });
}

/* ── Abrir Caja Modal ── */
function AbrirCajaModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const qc = useQueryClient();
  const [fondo, setFondo] = useState('0');

  const abrir = useMutation({
    mutationFn: (fondoInicial: number) => api.post('/api/caja/abrir', { fondoInicial }).then(r => r.data),
    onSuccess: () => {
      toast.success('Caja abierta');
      qc.invalidateQueries({ queryKey: ['sesion-caja-activa'] });
      qc.invalidateQueries({ queryKey: ['caja-historial'] });
      onSuccess();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al abrir caja')),
  });

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-semibold text-text-primary">Abrir caja</p>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">Fondo inicial (MXN)</label>
            <input
              type="number" min="0" step="0.01" value={fondo}
              onChange={e => setFondo(e.target.value)}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 font-mono"
            />
            <p className="text-xs text-text-secondary mt-1">Efectivo con el que empiezas la caja</p>
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button
            onClick={() => abrir.mutate(parseFloat(fondo) || 0)}
            disabled={abrir.isPending}
            className="flex-1 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-60">
            {abrir.isPending ? 'Abriendo...' : 'Abrir caja'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Cerrar Caja Modal ── */
function CerrarCajaModal({
  sesionId,
  resumen,
  onClose,
  onSuccess,
}: {
  sesionId: string;
  resumen: Resumen;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const qc = useQueryClient();
  const [obs, setObs] = useState('');

  const cerrar = useMutation({
    mutationFn: (observaciones: string) =>
      api.patch(`/api/caja/${sesionId}/cerrar`, { observaciones }).then(r => r.data),
    onSuccess: () => {
      toast.success('Caja cerrada');
      qc.invalidateQueries({ queryKey: ['sesion-caja-activa'] });
      qc.invalidateQueries({ queryKey: ['caja-historial'] });
      onSuccess();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al cerrar caja')),
  });

  const totalGeneral = resumen.totalEfectivo + resumen.totalTarjeta + resumen.totalTransferencia;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <p className="font-semibold text-text-primary">Cerrar caja</p>
          <button onClick={onClose}><X size={18} className="text-gray-400 hover:text-gray-600" /></button>
        </div>
        <div className="px-5 py-5 space-y-4">
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Resumen del turno</p>
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-1.5 text-text-secondary"><Banknote size={12}/> Efectivo</span>
              <span className="font-semibold">{fmt(resumen.totalEfectivo)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-1.5 text-text-secondary"><CreditCard size={12}/> Tarjeta</span>
              <span className="font-semibold">{fmt(resumen.totalTarjeta)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="flex items-center gap-1.5 text-text-secondary"><ArrowRightLeft size={12}/> Transferencia</span>
              <span className="font-semibold">{fmt(resumen.totalTransferencia)}</span>
            </div>
            <div className="flex justify-between text-sm border-t border-gray-200 pt-2 mt-2">
              <span className="font-bold text-text-primary">Total</span>
              <span className="font-bold text-primary-600">{fmt(totalGeneral)}</span>
            </div>
            <p className="text-xs text-text-secondary">{resumen.cantidadOperaciones} operaciones</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">Observaciones (opcional)</label>
            <textarea
              rows={2}
              value={obs}
              onChange={e => setObs(e.target.value)}
              placeholder="Notas del corte..."
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
            />
          </div>
        </div>
        <div className="flex gap-3 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-text-secondary hover:bg-gray-50 transition-colors">
            Cancelar
          </button>
          <button
            onClick={() => cerrar.mutate(obs)}
            disabled={cerrar.isPending}
            className="flex-1 py-2.5 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-60">
            {cerrar.isPending ? 'Cerrando...' : 'Cerrar caja'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── CorteCajaPage ── */
export function CorteCajaPage() {
  const [showAbrir, setShowAbrir] = useState(false);
  const [showCerrar, setShowCerrar] = useState(false);

  const { data: sesionActiva, isLoading: loadingSesion } = useQuery<SesionCaja | null>({
    queryKey: ['sesion-caja-activa'],
    queryFn: () => api.get('/api/caja/sesion-activa').then(r => r.data),
  });

  const { data: historial = [] } = useQuery<SesionCaja[]>({
    queryKey: ['caja-historial'],
    queryFn: () => api.get('/api/caja/historial').then(r => r.data),
  });

  const { data: resumen } = useQuery<Resumen>({
    queryKey: ['caja-resumen', sesionActiva?.id],
    queryFn: () => api.get(`/api/caja/${sesionActiva!.id}/resumen`).then(r => r.data),
    enabled: !!sesionActiva?.id,
  });

  return (
    <div className="space-y-6">
      {/* Estado actual */}
      {loadingSesion ? (
        <div className="bg-white rounded-xl border border-gray-100 p-6 text-center text-text-secondary text-sm">Cargando...</div>
      ) : sesionActiva ? (
        <div className="bg-white rounded-xl border border-green-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-xl flex items-center justify-center">
                <Unlock size={20} className="text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-text-primary">Caja abierta</p>
                <p className="text-xs text-text-secondary">Desde {fmtDate(sesionActiva.createdAt)}</p>
              </div>
            </div>
            <button
              onClick={() => setShowCerrar(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700 transition-colors">
              <Lock size={14} /> Cerrar caja
            </button>
          </div>

          {resumen && (
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                <Banknote size={16} className="text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-base font-bold text-text-primary">{fmt(resumen.totalEfectivo)}</p>
                  <p className="text-[10px] text-text-secondary uppercase tracking-wider">Efectivo</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                <CreditCard size={16} className="text-blue-600 flex-shrink-0" />
                <div>
                  <p className="text-base font-bold text-text-primary">{fmt(resumen.totalTarjeta)}</p>
                  <p className="text-[10px] text-text-secondary uppercase tracking-wider">Tarjeta</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3">
                <ArrowRightLeft size={16} className="text-purple-600 flex-shrink-0" />
                <div>
                  <p className="text-base font-bold text-text-primary">{fmt(resumen.totalTransferencia)}</p>
                  <p className="text-[10px] text-text-secondary uppercase tracking-wider">Transferencia</p>
                </div>
              </div>
              <div className="bg-primary-50 rounded-xl p-3 flex items-center gap-3">
                <TrendingUp size={16} className="text-primary-600 flex-shrink-0" />
                <div>
                  <p className="text-base font-bold text-primary-700">{fmt(resumen.totalEfectivo + resumen.totalTarjeta + resumen.totalTransferencia)}</p>
                  <p className="text-[10px] text-primary-600 uppercase tracking-wider">Total · {resumen.cantidadOperaciones} ops</p>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-amber-200 p-6 text-center">
          <Lock size={32} className="mx-auto text-amber-400 mb-3" />
          <p className="font-semibold text-text-primary mb-1">Caja cerrada</p>
          <p className="text-sm text-text-secondary mb-4">Abre la caja para poder registrar pagos</p>
          <button
            onClick={() => setShowAbrir(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-xl hover:bg-primary-700 transition-colors">
            <Unlock size={14} /> Abrir caja
          </button>
        </div>
      )}

      {/* Historial */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary mb-3">Historial de sesiones</h3>
        {historial.length === 0 ? (
          <p className="text-sm text-text-secondary italic">Sin sesiones registradas</p>
        ) : (
          <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
            {historial.map(s => (
              <div key={s.id} className="flex items-center gap-4 px-4 py-3.5">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${s.estado === 'abierta' ? 'bg-green-50' : 'bg-gray-50'}`}>
                  {s.estado === 'abierta'
                    ? <Unlock size={14} className="text-green-600" />
                    : <Lock size={14} className="text-gray-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary">{fmtDate(s.createdAt)}</p>
                  <p className="text-xs text-text-secondary">
                    {s.usuario ? `${s.usuario.nombre} ${s.usuario.apellidoPaterno}` : ''}{' '}
                    {s.fechaCierre ? `· Cierre: ${fmtDate(s.fechaCierre)}` : '· En curso'}
                  </p>
                </div>
                {s.totalGeneral !== null && (
                  <p className="font-bold text-text-primary text-sm flex-shrink-0">{fmt(s.totalGeneral)}</p>
                )}
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold flex-shrink-0 ${s.estado === 'abierta' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                  {s.estado === 'abierta' ? 'Abierta' : 'Cerrada'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {showAbrir && <AbrirCajaModal onClose={() => setShowAbrir(false)} onSuccess={() => setShowAbrir(false)} />}
      {showCerrar && resumen && sesionActiva && (
        <CerrarCajaModal
          sesionId={sesionActiva.id}
          resumen={resumen}
          onClose={() => setShowCerrar(false)}
          onSuccess={() => setShowCerrar(false)}
        />
      )}
    </div>
  );
}
